import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import Paginator from '../../../../components/Systema/Data/Paginator';
import { getAcaoGovernamental, getUnidadeJurisdicionada, getSituacao, getValorArrecadado } from '../emendaUtils';
import EmendaModal from './EmendaModal';

const sort = { exercicio: 'DESC' };
const limit = 10;
const backendLimit = 100000;

const formatValorBR = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '-';
  const numero = Number(valor);
  if (Number.isNaN(numero)) return valor;
  return numero.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatTextoLongo = (texto) => {
  if (!texto) return '-';
  return texto.length > 80 ? `${texto.substring(0, 80)}...` : texto;
};

const formatCodigoNome = (item) => {
  if (!item) return '-';
  if (item.codigo && item.nome) return `${item.codigo} - ${item.nome}`;
  return item.nome || item.codigo || '-';
};

const normalizarTexto = (valor) => `${valor || ''}`.toLowerCase().trim();

const contem = (valor, filtro) => {
  if (!filtro) return true;
  return normalizarTexto(valor).includes(normalizarTexto(filtro));
};

const igual = (valor, filtro) => {
  if (!filtro) return true;
  return normalizarTexto(valor) === normalizarTexto(filtro);
};



const filtrarEmendas = (data, filtros = {}) => data.filter((row) => (
  !!normalizarTexto(getSituacao(row)) &&
  igual(getUnidadeJurisdicionada(row), filtros.unidadeJurisdicionada) &&
  (!filtros.exercicio || `${row.exercicio || ''}` === `${filtros.exercicio}`) &&
  igual(row.emendaParlamentarTipo && row.emendaParlamentarTipo.nome, filtros.tipo) &&
  igual(
    (row.emendaParlamentarModalidadeExecucao && row.emendaParlamentarModalidadeExecucao.nome) || row.modalidadeExecucao,
    filtros.modalidadeExecucao
  ) &&
  contem(row.numeroEmenda, filtros.numero) &&
  contem(row.autoria, filtros.autoria) &&
  contem(row.detalhamentoObjeto, filtros.objeto) &&
  (!filtros.beneficiarioFinalId || `${(row.emendaParlamentarBeneficiario && row.emendaParlamentarBeneficiario.id) || ''}` === `${filtros.beneficiarioFinalId}`) &&
  igual(getSituacao(row), filtros.situacao)
));

export default function RelacaoEmendasTab({ identificador, service, page, setPage, filtros, onTotalizadoresChange }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emendaDetalhes, setEmendaDetalhes] = useState(null);

  useEffect(() => {
    if (!service) return;

    let isValid = true;
    setLoading(true);

    service.list({ sort, columns: [
        'id',
        'cliente.id', 'cliente.nome', 'cliente.nomeAbreviado',
        'exercicio',
        'emendaParlamentarTipo.id', 'emendaParlamentarTipo.nome', 
        'emendaParlamentarTipo.emendaParlamentarEP.codigo', 'emendaParlamentarTipo.emendaParlamentarEP.nome',
        'emendaParlamentarModalidadeExecucao.id', 'emendaParlamentarModalidadeExecucao.nome',
        
        'classificacaoOrcamentaria.unidadeOrcamentaria',
        'classificacaoOrcamentaria.funcao',
        'classificacaoOrcamentaria.subfuncao',
        'classificacaoOrcamentaria.programa',
        'classificacaoOrcamentaria.acao',
        'classificacaoOrcamentaria.despesa',
        'classificacaoOrcamentaria.fonteRecurso',

        'emendaParlamentarConcedente.id', 'emendaParlamentarConcedente.cnpj', 'emendaParlamentarConcedente.nome',
        'emendaParlamentarBeneficiario.id', 'emendaParlamentarBeneficiario.cnpj', 'emendaParlamentarBeneficiario.nome',

        'numeroEmenda',
        'numeroProposta',
        'autoria',
        'detalhamentoObjeto',
        'contaParaDeposito',
        'atoNormativoAprovacao',
        'dataDaEmenda',
        'observacao',
        'valorPrevisto',
        'emendaParlamentarSituacao.id', 'emendaParlamentarSituacao.nome',
        'valorAprovado',
        'emendaParlamentarExecucaoDespesa.totalEmpenhado',
        'emendaParlamentarExecucaoDespesa.totalLiquidado',
        'emendaParlamentarExecucaoDespesa.totalPago',
        'emendaParlamentarExecucaoReceita.valorArrecadado',
      ]}).then((res) => {
        if (!isValid) return;
        setData(res.data.list || []);
        setLoading(false);
      })
      .catch(() => {
        if (!isValid) return;
        setData([]);
        setLoading(false);
      });

    return () => {
      isValid = false;
    };
  }, [service]);
  
  const dataFiltrada = useMemo(() => filtrarEmendas(data, filtros), [data, filtros]);
  const lastPage = Math.ceil(dataFiltrada.length / limit);
  const pageData = useMemo(() => {
    const offset = (page - 1) * limit;
    return dataFiltrada.slice(offset, offset + limit);
  }, [dataFiltrada, page]);

  useEffect(() => {
    if (!loading && lastPage > 0 && page > lastPage) setPage(lastPage);
  }, [lastPage, loading, page, setPage]);

  useEffect(() => {
    if (onTotalizadoresChange) onTotalizadoresChange(dataFiltrada);
  }, [dataFiltrada, onTotalizadoresChange]);

  if (!service) {
    return (
      <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
        <Table className='table-bordered table-hover table-striped emenda-table'>
        <thead>
          <tr>
            <th className='align-middle text-center'>Detalhes</th>
            <th className='align-middle'>Unidade Jurisdicionada</th>
            <th className='align-middle'>Exercício</th>
            <th className='align-middle'>Tipo</th>
            <th className='align-middle'>Modalidade de Execução</th>
            <th className='align-middle'>Número</th>
            <th className='align-middle'>Autoria</th>
            <th className='align-middle'>Objeto</th>
            <th className='align-middle'>Situação</th>
            <th className='align-middle text-right'>Valor Aprovado</th>
            <th className='align-middle text-right'>Valor Arrecadado</th>
            <th className='align-middle text-right'>Valor Empenhado</th>
            <th className='align-middle text-right'>Valor Liquidado</th>
            <th className='align-middle text-right'>Valor Pago</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={15} className='text-center text-muted'>
              Identificador do portal não informado.
            </td>
          </tr>
        </tbody>
      </Table>
      </div>
    );
  }

  return (
    <Fragment>
      <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
        <Table className='table-bordered table-hover table-striped emenda-table'>
        <thead>
          <tr>
            <th className='align-middle text-center'>Detalhes</th>
            <th className='align-middle'>Unidade Jurisdicionada</th>
            <th className='align-middle'>Exercício</th>
            <th className='align-middle'>Tipo</th>
            <th className='align-middle'>EP</th>
            <th className='align-middle'>Modalidade de Execução</th>
            <th className='align-middle'>Número</th>
            <th className='align-middle'>Autoria</th>
            <th className='align-middle'>Objeto</th>
            <th className='align-middle'>Situação</th>
            <th className='align-middle text-right'>Valor Aprovado</th>
            <th className='align-middle text-right'>Valor Arrecadado</th>
            <th className='align-middle text-right'>Valor Empenhado</th>
            <th className='align-middle text-right'>Valor Liquidado</th>
            <th className='align-middle text-right'>Valor Pago</th>
          </tr>
        </thead>
        <tbody>
          {pageData.map((row, index) => (
            <tr key={index}>
              <td className='text-nowrap text-center'>
                <Button variant='outline-primary' size='sm' onClick={() => setEmendaDetalhes(row)}>
                  Detalhes
                </Button>
              </td>
              <td>{getUnidadeJurisdicionada(row)}</td>
              <td>{row.exercicio || '-'}</td>
              <td>{(row.emendaParlamentarTipo && row.emendaParlamentarTipo.nome) || '-'}</td>
              <td>{(row.emendaParlamentarTipo && row.emendaParlamentarTipo.emendaParlamentarEP && row.emendaParlamentarTipo.emendaParlamentarEP.codigo) || '-'}</td>
              <td>{(row.emendaParlamentarModalidadeExecucao && row.emendaParlamentarModalidadeExecucao.nome) || row.modalidadeExecucao || '-'}</td>
              <td>{row.numeroEmenda || '-'}</td>
              <td>{formatTextoLongo(row.autoria)}</td>
              <td>{formatTextoLongo(row.detalhamentoObjeto)}</td>
              <td>{formatTextoLongo(getSituacao(row))}</td>
              <td className='text-right'>{formatValorBR(row.valorAprovado)}</td>
              <td className='text-right'>{formatValorBR(getValorArrecadado(row))}</td>
              <td className='text-right'>{formatValorBR(row.totalEmpenhado)}</td>
              <td className='text-right'>{formatValorBR(row.totalLiquidado)}</td>
              <td className='text-right'>{formatValorBR(row.totalPago)}</td>
            </tr>
          ))}
          {!loading && pageData.length === 0 && (
            <tr>
              <td colSpan={16} className='text-center text-muted'>
                Nenhuma emenda parlamentar encontrada.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      </div>
      <Paginator page={page} lastPage={lastPage} setPage={setPage} loading={loading} />
      <EmendaModal
        identificador={identificador}
        emenda={emendaDetalhes}
        onHide={() => setEmendaDetalhes(null)}
      />
    </Fragment>
  );
}
